import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

const MENU_WIDTH = 160;

export interface StatusConfig {
  id: string;
  name: string;
  color: string;
  position?: number;
  isClosed: boolean;
}

export interface StatusPickerProps {
  statusConfig?: StatusConfig | null;
  statusConfigs: StatusConfig[];
  onChange?: (statusId: string) => void;
  stopDragPointerDown?: boolean;
}

export function StatusPicker({
  statusConfig,
  statusConfigs,
  onChange,
  stopDragPointerDown = true,
}: StatusPickerProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const current = statusConfig ?? statusConfigs.find((s) => !s.isClosed) ?? statusConfigs[0];
  const editable = !!onChange;

  const updatePosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const rightEdge = rect.left + MENU_WIDTH;
    const left =
      rightEdge > window.innerWidth - 8
        ? Math.max(8, window.innerWidth - MENU_WIDTH - 8)
        : rect.left;
    setPos({ top: rect.bottom + 4, left });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updatePosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(t) &&
        buttonRef.current && !buttonRef.current.contains(t)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  if (!current) return null;

  const chipStyle = { backgroundColor: `${current.color}20`, color: current.color };

  const chipContent = (
    <>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: current.color }} />
      {current.name}
    </>
  );

  if (!editable) {
    return (
      <span
        className="flex items-center gap-1 flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full"
        style={chipStyle}
      >
        {chipContent}
      </span>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onPointerDown={stopDragPointerDown ? (e) => e.stopPropagation() : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex items-center gap-1 flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full transition-opacity hover:opacity-80"
        style={chipStyle}
      >
        {chipContent}
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_WIDTH }}
          className="bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {[...statusConfigs]
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(option.id);
                  setOpen(false);
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-grey-50 transition-colors text-secondary',
                  current.id === option.id && 'font-medium',
                )}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: option.color }}
                />
                {option.name}
              </button>
            ))}
        </div>,
        document.body,
      )}
    </>
  );
}
